<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <h1 class="pb-15">
        <v-svg class="voxo-red" sprite="megaphone" />
        Announcements
      </h1>
      <div class="flex pb-20">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable> </el-input>
        </div>
        <div class="box right">
          <el-button @click="openCreateModal">Add New</el-button>
        </div>
      </div>

      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>

      <create-form
        :ivr-options="ivrOptions"
        :branch-options="branchOptions"
        :createVisible="createVisible"
        :createProcessing="createProcessing"
        :clearFormFields="clearFormFields"
        @cancelOperation="cancelOperation"
        @createOperation="createOperation"
        :get-ivr-options="getIvrOptions"
      />

      <edit-form
        :ivr-options="ivrOptions"
        :branch-options="branchOptions"
        :editVisible="editVisible"
        :editProcessing="editProcessing"
        :clearFormFields="clearFormFields"
        :selectedAnnouncement="selectedAnnouncement"
        :get-ivr-options="getIvrOptions"
        @cancelOperation="cancelOperation"
        @editOperation="editOperation"
        @updateIvrOptions="updateIvrOptions"
      />
    </div>
  </div>
</template>

<script>
import { debounce } from "lodash";
import moment from "moment";
import CreateForm from "./components/create-form";
import EditForm from "./components/edit-form";

import feathersClient, { apiV2Host } from "../../../feathers/feathers-client";
import { globalEventBus } from "../../../globalEventBus";

export default {
  name: "AnnouncementsTable",
  data() {
    return {
      total: 0,
      eventChannel: null,
      selectedAnnouncement: null,
      tableData: [],
      announcements: [],
      ivrOptions: [],
      branchOptions: [],
      search: "",
      visibleActionsId: "",
      editVisible: false,
      editProcessing: false,
      createVisible: false,
      createProcessing: false,
      clearFormFields: false,
      tableLoading: false,
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: ({ row }) => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-red" sprite="megaphone" />
                </div>
              ];
            }
          }
        },
        {
          field: "name",
          title: "Name",
          minWidth: 150,
          sortable: true
        },
        {
          field: "actions",
          title: "",
          minWidth: "150",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={"edit-" + row.id}
                    icon="el-icon-edit"
                    onClick={() => this.openEditModal(row.id)}
                    class="table-action-button icon-blue"
                  />
                  <el-button
                    id={"delete-" + row.id}
                    icon="el-icon-delete"
                    onClick={() => this.deleteOperation(row.id)}
                    class="table-action-button icon-red"
                  />
                </div>
              ];
            }
          }
        }
      ]
    };
  },

  components: {
    CreateForm,
    EditForm
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    user() {
      return this.$store.state.auth.user;
    }
  },

  watch: {
    selectedTenant() {
      this.findAnnouncements();
      this.getIvrOptions();
      this.getBranches();
    },
    user() {
      this.findAnnouncements();
      this.getIvrOptions();
      this.getBranches();
    },
    search: debounce(function () {
      this.searchAnnouncements();
    }, 500)
  },

  methods: {
    searchAnnouncements() {
      this.findAnnouncements(this.search);
    },

    openCreateModal() {
      this.createVisible = true;
      this.clearFormFields = false;
    },

    async openEditModal(id) {
      try {
        const res = await fetch(`${apiV2Host}/admin/announcements/${id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
            Accept: "application/json"
          }
        });

        const response = await res.json();

        if (!res.ok) {
          this.tableLoading = false;
          const { name, message, validationErrors } = response;
          this.$notify({
            title: name,
            message:
              validationErrors && validationErrors.length
                ? validationErrors[0]
                : message,
            type: "error"
          });
          return;
        }

        this.selectedAnnouncement = response;
        this.clearFormFields = false;
        this.editVisible = true;
      } catch (err) {
        this.tableLoading = false;
      }
    },

    //General Form and Table Operations
    cellClicked(data) {
      if (data.column.property !== "actions") {
        this.openEditModal(data.row.id);
      }
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },

    async findAnnouncements(name = null) {
      this.tableLoading = true;
      this.tableData = [];
      let url = `${apiV2Host}/admin/announcements?tenantId=${this.selectedTenant.id}&recordsPerPage=100`;
      if (name) url += `&name=${name}`;
      try {
        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
            Accept: "application/json"
          }
        });

        const response = await res.json();

        if (!res.ok) {
          this.tableLoading = false;
          const { name, message, validationErrors } = response;
          this.$notify({
            title: name,
            message:
              validationErrors && validationErrors.length
                ? validationErrors[0]
                : message,
            type: "error"
          });
          return;
        }

        const { records } = response;

        this.tableData = [...records];
        this.announcements = [...records];
        this.total = records.length;
        this.tableLoading = false;
      } catch (err) {
        this.tableLoading = false;
      }
    },

    getBranches() {
      this.branchOptions = [];
      feathersClient
        .service("branches")
        .find({
          query: {
            tenantId: this.selectedTenant.id,
            $sort: { name: 1 }
          }
        })
        .then((res) => {
          this.branchOptions = [...res];
        }).catch(e => {
          this.branchOptions = [];
      });
    },

    async getIvrOptions(query = "") {
      try {
        let url = `${apiV2Host}/admin/virtual-receptionist?tenantId=${this.selectedTenant.id}`;

        if (query && query.trim()) {
          url += `&name=${query}`;
        }

        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
            Accept: "application/json"
          }
        });

        const response = await res.json();

        if (!res.ok) {
          const { name, message, validationErrors } = response;
          this.$notify({
            title: name,
            message:
              validationErrors && validationErrors.length
                ? validationErrors[0]
                : message,
            type: "error"
          });
          return;
        }

        const { records } = response;

        this.ivrOptions = [...records];
      } catch (err) {
        this.ivrOptions = [];
      }
    },

    updateIvrOptions(ivrs) {
      // This populates the ivrOptions with the options that are selected in the child component, allowing the name to be shown
      const existingIds = new Set(this.ivrOptions.map((ivr) => ivr.id));
      const newIvrs = ivrs.filter((ivr) => !existingIds.has(ivr.id));
      this.ivrOptions = [...this.ivrOptions, ...newIvrs];
    },

    // ⭐️ Create Operation ⭐️ //
    createOperation(createBody) {
      this.$confirm("Create announcement?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(async () => {
          this.createProcessing = true;

          const postBody = {
            name: createBody.name,
            description: createBody.description,
            tenantId: this.selectedTenant.id,
            active: !createBody.active ? 0 : 1,
            startDate: moment(createBody.dateRange[0]).format(
              "YYYY-MM-DDTHH:mm:ss"
            ),
            endDate: moment(createBody.dateRange[1]).format(
              "YYYY-MM-DDTHH:mm:ss"
            ),
            ivrIds: createBody.ivrIds,
            mediaFileId: createBody.mediaFileId
          };

          try {
            const res = await fetch(
              `${apiV2Host}/admin/announcements?tenantId=${this.selectedTenant.id}`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
                  Accept: "application/json"
                },
                body: JSON.stringify(postBody)
              }
            );
            const response = await res.json();

            if (!res.ok) {
              this.$notify({
                title: response.name,
                message: response.message,
                type: "error"
              });
              this.createProcessing = false;
              return;
            }

            this.$notify({
              title: "Success",
              message: "Announcement created successfully!",
              type: "success"
            });

            this.tableData.push(response);
            this.announcements.push(response);
            this.total = this.announcements.length;
            this.createProcessing = false;
            this.cancelOperation("create");
          } catch (error) {
            if (error.hasOwnProperty("errors")) {
              this.$notify({
                title: error.message,
                message: error.errors[Object.keys(error.errors)[0]],
                type: "error"
              });
            } else {
              this.$notify({
                title: error.name,
                message: error.message,
                type: "error"
              });
            }
            this.createProcessing = false;
            this.clearFormFields = false;
          }
        })
        .catch(() => true);
    },

    // ⭐️ Edit Operation ⭐️ //
    editOperation(editBody) {
      this.$confirm("Save announcement?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(async () => {
          this.editProcessing = true;

          const patchBody = {
            name: editBody.name,
            description: editBody.description,
            active: !editBody.active ? 0 : 1,
            branchId: editBody.branchId,
            startDate: moment(editBody.dateRange[0]).format(
              "YYYY-MM-DDTHH:mm:ss"
            ),
            endDate: moment(editBody.dateRange[1]).format(
              "YYYY-MM-DDTHH:mm:ss"
            ),
            ivrIds: editBody.ivrIds,
            mediaFileId: editBody.mediaFileId
          };

          try {
            const res = await fetch(
              `${apiV2Host}/admin/announcements/${this.selectedAnnouncement.id}`,
              {
                method: "PATCH",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
                  Accept: "application/json"
                },
                body: JSON.stringify(patchBody)
              }
            );
            const response = await res.json();

            if (!res.ok) {
              this.$notify({
                title: response.name,
                message: response.message,
                type: "error"
              });
              this.editProcessing = false;
              return;
            }

            const updateIndex = this.tableData.findIndex(
              (item) => item.id === response.id
            );

            const updateAllIndex = this.announcements.findIndex(
              (item) => item.id === response.id
            );

            if (updateIndex >= 0) {
              this.tableData.splice(updateIndex, 1, response);
            }

            if (updateAllIndex >= 0) {
              this.announcements.splice(updateAllIndex, 1, response);
            }

            this.$notify({
              title: "Success",
              message: "Announcement updated successfully!",
              type: "success"
            });

            this.editProcessing = false;
            this.cancelOperation("edit");
          } catch (error) {
            console.log(`our error`, error);
            if (error.hasOwnProperty("errors")) {
              this.$notify({
                title: error.message,
                message: error.errors[Object.keys(error.errors)[0]],
                type: "error"
              });
            } else {
              this.$notify({
                title: error.name,
                message: error.message,
                type: "error"
              });
            }
            this.editProcessing = false;
            this.clearFormFields = false;
          }
        })
        .catch(() => true);
    },

    // ⭐️ Delete Operation ⭐️ //
    async deleteOperation(id) {
      this.$confirm("Delete announcement?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(async () => {
          this.tableLoading = true;
          try {
            const res = await fetch(`${apiV2Host}/admin/announcements/${id}`, {
              method: "DELETE",
              headers: {
                Authorization: `Bearer ${this.$store.state.auth.accessToken}`
              }
            });

            const response = await res.json();

            this.tableData = this.tableData.filter((data) => data.id !== id);

            this.tableLoading = false;

            if (!res.ok) {
              this.$notify({
                title: response.name,
                message: response.message,
                type: "error"
              });
              return;
            }

            this.$notify({
              title: "Success",
              message: "Announcement deleted successfully!",
              type: "success"
            });
            this.tableLoading = false;
          } catch (error) {
            if (error.hasOwnProperty("errors")) {
              this.$notify({
                title: error.message,
                message: error.errors[Object.keys(error.errors)[0]],
                type: "error"
              });
            } else {
              this.$notify({
                title: error.name,
                message: error.message,
                type: "error"
              });
            }
            this.tableLoading = false;
          }
        })
        .catch(() => true);
    },

    // ⭐️ Cancel Operation ⭐️ //
    cancelOperation(type) {
      if (type === "create") {
        this.createVisible = false;
      } else if (type === "edit") {
        this.editVisible = false;
      }
      this.clearFormFields = true;
    },

    removeListeners() {
      globalEventBus.$off("appReconnected");
    }
  },

  mounted() {
    if (this.user) {
      this.findAnnouncements();
      this.getIvrOptions();
      this.getBranches();
    }
  },

  beforeDestroy() {
    this.removeListeners();
  }
};
</script>
